/**
 * Event-anchored field-capacity detection.
 *
 * When water is applied to a zone — irrigation or rain — the probe reading
 * spikes above field capacity as the soil briefly saturates. Gravity then
 * drains the excess over hours down to a PLATEAU, and that plateau is field
 * capacity as this probe reads it (the correct `moistureHighPct`). After the
 * plateau, ET draws the reading down slowly. So the physics is spike → drain →
 * plateau → slow decline, and the plateau — not the transient peak — is the
 * value we want.
 *
 * Because we know exactly WHEN water was applied (run records, rain days),
 * detection is event-anchored rather than statistical: for each wetting event
 * we look at the readings that follow it, find the saturation peak, then walk
 * forward until the reading stops falling steeply (drainage done) and settles
 * into a flat span (the plateau). This beats the old p95-of-a-month approach,
 * which is biased by watering frequency and can't tell a wet month from a
 * sensor that reads high.
 *
 * Everything here operates on HOURLY-BUCKETED MEANS of the readings. That
 * dedupes 15-minute reporting noise, and it means the same code works directly
 * on the API's hourly-bucketed history payload.
 *
 * The baseline (pre-event dry value) is the LOWEST bucket before the peak, for
 * the same reason `sensor-response.ts` takes a window minimum: a run's recorded
 * start can lag the water reaching the probe, so the last pre-event sample may
 * already be mid-spike. Moisture only rises with water, so the pre-peak minimum
 * is the true dry value.
 *
 * Like `evaluateSensorResponse`, plateau detection has a "too early" contract:
 * readings lag, so before the drainage window has elapsed we return TOO_EARLY
 * rather than prematurely declaring failure. A qualifying plateau that is
 * already in hand resolves FOUND even before the window ends, but only once its
 * settle has actually been CONFIRMED by data past the look-ahead horizon (see
 * {@link PLATEAU_SETTLE_LOOKAHEAD_HOURS}). Confirmation is the one thing that
 * outlives the drainage window: an unconfirmed flat span stays TOO_EARLY even
 * after the window has elapsed, because "the clock ran out" is not evidence the
 * reading stopped falling. On a soil whose gravity drainage is slower than its
 * ET drawdown, that distinction is the only one available: no slope test can
 * separate "still draining" from "settled and drying", so the separation has to
 * come from having looked, rather than from the shape of what was seen.
 */
import { DRAINAGE_HOURS } from './tables.js';
const HOUR_MS = 3_600_000;
/**
 * Minimum peak − baseline rise (percentage points) for an event to count. Below
 * this the water didn't measurably reach the probe (clog, mis-placed sensor,
 * run too short), so the observation is discarded. Matches the default sensor-
 * response floor: a live probe under real irrigation moves several points.
 */
export const CALIBRATION_MIN_RISE_PCT = 2;
/**
 * Maximum |slope| (percentage points per hour) for a bucket-to-bucket span to
 * count as a plateau. Gravity drainage right after saturation runs well above
 * this (points per hour); ET drawdown on a hot day is only ~0.2 pp/hr. 0.3
 * pp/hr sits between the two, so it catches the settled plateau without waiting
 * for the slow ET decline to look flat.
 */
export const PLATEAU_SLOPE_MAX_PCT_PER_HR = 0.3;
/**
 * Minimum width (hours) a flat span must cover before it is trusted as the
 * field-capacity plateau — the "settle gate".
 *
 * A single two-bucket step under {@link PLATEAU_SLOPE_MAX_PCT_PER_HR} is not
 * enough: a slow-draining ("soupy") zone lingers above field capacity for hours
 * after heavy rain, drifting down at a rate that momentarily dips under the
 * slope bound before resuming its fall. That transient shoulder is NOT field
 * capacity — the soil is still shedding gravitational water — yet recording it
 * would push the calibrated "full" up toward saturation. (This is the mirror
 * image of the under-delivery *floor* guarded in calibration.ts: there a run's
 * sub-capacity ceiling is mistaken for "full" from below; here a still-draining
 * shelf is mistaken for it from above.) Requiring the flatness to hold for a
 * sustained span forces the reading to actually equilibrate before we trust it;
 * genuine drainage-to-FC flattens and stays flat, a soupy shoulder does not.
 *
 * This is the weaker of the two settle guards — a floor that rejects a lone
 * two-bucket blip; {@link PLATEAU_SETTLE_DROP_PCT} does the heavier lifting by
 * rejecting a longer shoulder that later resumes falling.
 */
export const PLATEAU_MIN_SPAN_HOURS = 2;
/**
 * Confirmation guard on top of {@link PLATEAU_MIN_SPAN_HOURS}: even a sustained
 * flat span is rejected if the reading then resumes falling by more than
 * {@link PLATEAU_SETTLE_DROP_PCT} within {@link PLATEAU_SETTLE_LOOKAHEAD_HOURS}
 * after it. Slow ET drawdown drifts down only ~0.2 pp/hr (well under the drop
 * threshold over the look-ahead); resumed gravity drainage keeps dropping past
 * it. So a shoulder that briefly flattens and then keeps sinking is caught here
 * even if it happened to hold flat for the minimum span.
 */
export const PLATEAU_SETTLE_LOOKAHEAD_HOURS = 6;
export const PLATEAU_SETTLE_DROP_PCT = 1.5;
/**
 * A rain day must drop at least this much (inches) to be treated as a wetting
 * event. Lighter rain may not saturate the root zone to field capacity, so it
 * would anchor a plateau below the true value. 0.25 in is a meaningful soak.
 */
export const RAIN_EVENT_MIN_IN = 0.25;
/**
 * How many successful plateau observations we need before trusting the event-
 * anchored median over the p95 fallback. Three independent wetting events
 * agreeing filters out a single fluke (an interrupted drain, a bad reading).
 */
export const MIN_EVENT_OBSERVATIONS = 3;
/**
 * How much (percentage points) an unexplained rise must add up to before it is
 * read as water arriving. See {@link inferWettingEvents}.
 *
 * Deliberately its own constant even though it currently equals
 * {@link CALIBRATION_MIN_RISE_PCT}. That one is a calibration GATE, asking
 * whether an event we already know about moved the probe enough to be worth
 * measuring. This one is a DETECTION floor, asking whether a rise is water at
 * all or just probe noise and hourly-mean wobble. The two answer different
 * questions and are free to move apart.
 */
export const INFERRED_EVENT_MIN_RISE_PCT = 2;
/**
 * Slack (ms) around a known event's recorded span when deciding whether a rise
 * is already explained. A run's recorded start can lag the water reaching the
 * probe, and a webhook's timestamps can be off by minutes, so a rise that
 * begins just outside the recorded span still belongs to it. The pad is
 * deliberately NOT the drainage window: a rise during an event's drainage TAIL
 * is new water landing on draining ground, which is exactly what we are hunting.
 */
const KNOWN_EVENT_PAD_MS = HOUR_MS;
const round1 = (n) => Math.round(n * 10) / 10;
/**
 * Collapse readings (any order/cadence) into hourly-bucketed means, sorted
 * ascending by hour. Each reading is floored to the top of its hour.
 */
export function bucketHourlyMeans(readings) {
    const sums = new Map();
    for (const r of readings) {
        const t = Date.parse(r.at);
        if (!Number.isFinite(t))
            continue;
        const hourMs = Math.floor(t / HOUR_MS) * HOUR_MS;
        const cur = sums.get(hourMs);
        if (cur) {
            cur.sum += r.moisturePct;
            cur.count += 1;
        }
        else {
            sums.set(hourMs, { sum: r.moisturePct, count: 1 });
        }
    }
    return [...sums.entries()]
        .map(([hourMs, { sum, count }]) => ({ hourMs, mean: sum / count }))
        .sort((a, b) => a.hourMs - b.hourMs);
}
/** True when the two adjacent buckets are flat enough to be one plateau span. */
function flatBetween(a, b) {
    const hours = (b.hourMs - a.hourMs) / HOUR_MS;
    if (hours <= 0)
        return false;
    return Math.abs(b.mean - a.mean) / hours <= PLATEAU_SLOPE_MAX_PCT_PER_HR;
}
/**
 * Find the field-capacity plateau following a single wetting event.
 *
 * Search window: [event.start, min(event.end + DRAINAGE_HOURS[soil],
 * nextEventStart, now)]. Peak = max bucket in the window; baseline = min bucket
 * in [event.start − 1h, peak]. If the rise is too small the observation is
 * discarded. Otherwise walk buckets starting ≥ 1h after the peak and take the
 * first span of ≥ 2 consecutive buckets whose slope magnitude stays within
 * {@link PLATEAU_SLOPE_MAX_PCT_PER_HR}; its mean is the plateau.
 *
 * The span must also be CONFIRMED: the settle check has to have had something
 * to look at. A span with nothing after it passes "did it resume falling?" by
 * default, which is not the same answer as "no, it held". So a sustained,
 * settled span resolves FOUND only when either the next wetting event starts
 * inside the look-ahead (the soil is being re-wet, so the question is moot) or a
 * bucket exists at or past the look-ahead horizon. Otherwise the result is
 * TOO_EARLY, window elapsed or not, and the search stops there: any bucket that
 * could confirm a LATER span (whose end is no earlier) would confirm this one
 * first, so scanning on could only find spans that are equally unconfirmable.
 */
export function findPlateau(params) {
    const { event, readings, soilType, now, nextEventStart } = params;
    const startMs = Date.parse(event.start);
    const endMs = Date.parse(event.end);
    const nowMs = Date.parse(now);
    const drainMs = DRAINAGE_HOURS[soilType] * HOUR_MS;
    const nextMs = nextEventStart ? Date.parse(nextEventStart) : Infinity;
    // The window's natural end (drainage done, or the next event, whichever is
    // first). We only *look at* data up to `now`, but "has the window elapsed?"
    // is judged against the natural end so capping at `now` can't hide TOO_EARLY.
    const naturalEndMs = Math.min(endMs + drainMs, nextMs);
    const searchEndMs = Math.min(naturalEndMs, nowMs);
    const windowElapsed = nowMs >= naturalEndMs;
    const buckets = bucketHourlyMeans(readings);
    const startHour = Math.floor(startMs / HOUR_MS) * HOUR_MS;
    // Peak: highest bucket in [event.start, searchEnd]. Keep the earliest on ties
    // so the drainage walk that follows it has the most room.
    let peak;
    for (const b of buckets) {
        if (b.hourMs < startHour || b.hourMs > searchEndMs)
            continue;
        if (peak === undefined || b.mean > peak.mean)
            peak = b;
    }
    if (peak === undefined) {
        // No usable readings in the window yet, so nothing observed and no rise.
        return windowElapsed ? { kind: 'NO_PLATEAU', riseSeen: false } : { kind: 'TOO_EARLY', riseSeen: false };
    }
    // Baseline: lowest bucket in [event.start − 1h, peak time] (includes the peak
    // bucket, so it is always defined once a peak exists).
    const baseFromHour = startHour - HOUR_MS;
    let baseline = peak.mean;
    for (const b of buckets) {
        if (b.hourMs < baseFromHour || b.hourMs > peak.hourMs)
            continue;
        if (b.mean < baseline)
            baseline = b.mean;
    }
    const rise = peak.mean - baseline;
    if (rise < CALIBRATION_MIN_RISE_PCT) {
        // Not enough rise. Terminal only once we've given readings time to arrive.
        return windowElapsed ? { kind: 'NO_RISE' } : { kind: 'TOO_EARLY', riseSeen: false };
    }
    // Plateau: the SETTLED field-capacity value (see PLATEAU_MIN_SPAN_HOURS), not
    // the first flat step on the way down. Walk buckets from ≥ 1h after the peak
    // (skipping the steep drop right off the peak), collect each maximal flat
    // span, and take the first that is BOTH:
    //   (a) sustained — flat across ≥ PLATEAU_MIN_SPAN_HOURS, not a lone two-bucket
    //       step a soupy shoulder can fake, and
    //   (b) settled — has not, in the data we can see, resumed falling by more
    //       than PLATEAU_SETTLE_DROP_PCT within PLATEAU_SETTLE_LOOKAHEAD_HOURS
    //       after it (still-draining shoulders keep sinking; a real plateau only
    //       drifts down slowly with ET), and
    //   (c) confirmed: that settle check actually had data to read, rather than
    //       passing because there was nothing after the span to contradict it.
    const tail = buckets.filter((b) => b.hourMs >= peak.hourMs + HOUR_MS && b.hourMs <= searchEndMs);
    // The settle check gets a wider view than the plateau search itself. A span
    // that lands against the window's edge has almost no data after it, so the
    // "did it resume falling?" test would pass by default and a still-draining
    // shoulder could be recorded as field capacity purely because the drainage
    // clock ran out. Drainage does not stop at the window edge, so we let the
    // check read the readings that follow it, out to the next wetting event (past
    // which the soil is being re-wet and the question is moot) or `now`. The
    // plateau SEARCH stays bounded by `searchEndMs`; what the wider view buys is
    // the ability to tell "it held" from "nobody looked", which is what gate (c)
    // below turns into a verdict.
    const lookEndMs = Math.min(nextMs, nowMs);
    const settleTail = buckets.filter((b) => b.hourMs >= peak.hourMs + HOUR_MS && b.hourMs <= lookEndMs);
    const minSpanMs = PLATEAU_MIN_SPAN_HOURS * HOUR_MS;
    const lookaheadMs = PLATEAU_SETTLE_LOOKAHEAD_HOURS * HOUR_MS;
    let span;
    for (let i = 0; i < tail.length - 1; i++) {
        if (!flatBetween(tail[i], tail[i + 1]))
            continue;
        let j = i + 1;
        while (j < tail.length - 1 && flatBetween(tail[j], tail[j + 1]))
            j++;
        const candidate = tail.slice(i, j + 1);
        i = j; // resume past this span; its trailing gap is already known non-flat
        const last = candidate[candidate.length - 1];
        // (a) Sustained.
        if (last.hourMs - candidate[0].hourMs < minSpanMs)
            continue;
        // (b) Settled — reject a shoulder that resumes falling within the look-ahead.
        const mean = candidate.reduce((s, b) => s + b.mean, 0) / candidate.length;
        const resumesFalling = settleTail.some((b) => b.hourMs > last.hourMs && b.hourMs <= last.hourMs + lookaheadMs && b.mean < mean - PLATEAU_SETTLE_DROP_PCT);
        if (resumesFalling)
            continue;
        // (c) Confirmed: the look-ahead was observable. Either the next event
        // starts inside it (re-wetting makes the question moot) or we hold a bucket
        // at/past the horizon, which together with the resumesFalling scan above
        // means we watched the whole look-ahead and the fall did not restart. With
        // neither, "settled" is only "unrefuted", and on a soil that drains slower
        // than it dries there is no local slope that would refute it. So keep
        // waiting: TOO_EARLY even once the drainage window has elapsed, because the
        // confirmation clock runs past that window. Return rather than continue,
        // because a later span ends no earlier: nothing that fails to confirm this
        // one could confirm that one either.
        const confirmed = nextMs <= last.hourMs + lookaheadMs || settleTail.some((b) => b.hourMs >= last.hourMs + lookaheadMs);
        if (!confirmed)
            return { kind: 'TOO_EARLY', riseSeen: true };
        span = candidate;
        break;
    }
    if (span === undefined) {
        // Rise seen but no settled plateau yet — still draining, or a soupy event
        // that never equilibrated within its drainage window. Either way the water
        // that went in is still moving, which is what `riseSeen` tells callers.
        return windowElapsed ? { kind: 'NO_PLATEAU', riseSeen: true } : { kind: 'TOO_EARLY', riseSeen: true };
    }
    const plateauPct = span.reduce((s, b) => s + b.mean, 0) / span.length;
    const observation = {
        eventAt: event.source === 'RAIN' ? event.start : event.end,
        source: event.source,
        baselinePct: round1(baseline),
        peakPct: round1(peak.mean),
        plateauPct: round1(plateauPct),
        plateauAt: new Date(span[0].hourMs).toISOString(),
    };
    if (event.runNetDepthIn !== undefined)
        observation.runNetDepthIn = event.runNetDepthIn;
    return { kind: 'FOUND', observation };
}
/**
 * Run {@link findPlateau} over a sorted event list (each window truncated at
 * the next event's start) and return every outcome, successful or not.
 *
 * The failures matter as much as the successes. A window that saw water go in
 * and never settle is direct evidence that the readings around it are still
 * draining, which is the difference between a probe reading high because the
 * soil holds a lot of water and a probe reading high because the water has not
 * left yet. {@link detectFieldCapacityObservations} keeps only the successes;
 * callers that need to reason about WHY the event path came up short (see
 * calibration.ts) take the outcomes.
 */
export function detectFieldCapacityOutcomes(events, readings, soilType, now) {
    const sorted = [...events].sort((a, b) => Date.parse(a.start) - Date.parse(b.start));
    const nowIso = now.toISOString();
    return sorted.map((event, i) => ({
        event,
        result: findPlateau({ event, readings, soilType, now: nowIso, nextEventStart: sorted[i + 1]?.start }),
    }));
}
/**
 * Run {@link findPlateau} over a sorted event list (each window truncated at
 * the next event's start) and return the successful observations.
 */
export function detectFieldCapacityObservations(events, readings, soilType, now) {
    const observations = [];
    for (const { result } of detectFieldCapacityOutcomes(events, readings, soilType, now)) {
        if (result.kind === 'FOUND')
            observations.push(result.observation);
    }
    return observations;
}
/**
 * Read the wetting events nobody recorded straight off the probe.
 *
 * Soil moisture only rises when water arrives. That is the whole premise of the
 * plateau physics above, and it cuts both ways: a sustained rise that no run and
 * no rain day accounts for IS a wetting event, whatever put the water there.
 * Prod supplies the case: on 2026-08-22 the Back Lawn probe climbed out of the
 * mid 30s to 47 across a single morning with `rainIn` zero on every nearby
 * snapshot and no run of any kind. Rain the snapshot pipeline missed and someone
 * hand-watering look identical on the wire, and both are real water.
 *
 * Leaving such a soak out of the event list is not neutral. Its drainage window
 * is never censored, so hours of gravitational water sit in the percentile pool
 * claiming to be soil at rest; it lands inside a neighboring event's search
 * window and poisons that event's baseline; and when it fails to settle, the
 * evidence that this soil drains slower than modeled never reaches the raise
 * gate. Detecting it fixes all three at once.
 *
 * The search is deliberately coarse: walk the hourly means, take each maximal
 * run of rising buckets, and keep the ones that climb at least
 * {@link INFERRED_EVENT_MIN_RISE_PCT} from foot to crest and do not START inside
 * an already-known event's padded span. The event spans foot to crest, which is
 * when the water was demonstrably arriving. Depth is unknowable, so none is
 * recorded, and `reachedFieldCapacity` refuses to calibrate off these for that
 * reason: without a depth there is no soak gate and no overshoot test, so a
 * plateau after an INFERRED event could be a sub-capacity ceiling and we would
 * never know.
 */
export function inferWettingEvents(readings, knownEvents) {
    const buckets = bucketHourlyMeans(readings);
    const explained = knownEvents.map((e) => ({
        from: Date.parse(e.start) - KNOWN_EVENT_PAD_MS,
        to: Date.parse(e.end) + KNOWN_EVENT_PAD_MS,
    }));
    const candidates = [];
    for (let i = 0; i < buckets.length - 1; i++) {
        if (buckets[i + 1].mean <= buckets[i].mean)
            continue;
        // Maximal rising run: the foot is a local minimum by construction (we only
        // get here from a step that was not rising, or from the previous crest).
        let j = i + 1;
        while (j < buckets.length - 1 && buckets[j + 1].mean > buckets[j].mean)
            j++;
        const foot = buckets[i];
        const crest = buckets[j];
        i = j - 1; // resume scanning at the crest; the loop's ++ lands there
        if (crest.mean - foot.mean < INFERRED_EVENT_MIN_RISE_PCT)
            continue;
        // A rise that starts inside a recorded event's span is that event's water.
        if (explained.some((e) => foot.hourMs >= e.from && foot.hourMs <= e.to))
            continue;
        candidates.push({ startMs: foot.hourMs, endMs: crest.hourMs });
    }
    // Maximal runs cannot overlap, but merging keeps that an invariant of the
    // OUTPUT rather than of the walk, so a later change to the scan cannot quietly
    // hand callers two events describing one soak.
    const merged = [];
    for (const c of candidates.sort((a, b) => a.startMs - b.startMs)) {
        const last = merged[merged.length - 1];
        if (last && c.startMs <= last.endMs)
            last.endMs = Math.max(last.endMs, c.endMs);
        else
            merged.push({ ...c });
    }
    return merged.map((m) => ({
        start: new Date(m.startMs).toISOString(),
        end: new Date(m.endMs).toISOString(),
        source: 'INFERRED',
    }));
}
/**
 * The event list the CALIBRATION flow reasons over: everything recorded, plus
 * every unexplained rise.
 *
 * One helper so the daily pass and the on-demand check cannot drift into
 * disagreeing about what counts as a wetting event, which would let a zone read
 * one way in the nightly summary and another way the moment a user pressed
 * "check". Calibration only: `assessPrecipRate` compares a run's plateau against
 * the depth that run intended to deliver, and an inferred event has no depth, so
 * feeding it one would be comparing a measurement to nothing.
 */
export function calibrationWettingEvents(readings, knownEvents) {
    return [...knownEvents, ...inferWettingEvents(readings, knownEvents)];
}
/**
 * Whether an outcome proves the soil was still shedding gravitational water
 * when the search ran out of window (or out of data).
 *
 * Water measurably reached the probe and the reading never came to rest. It
 * does not matter which way the search failed: NO_PLATEAU means the drainage
 * clock expired mid-fall, TOO_EARLY-with-rise means the fall is in flight right
 * now. Both say the same physical thing: the readings near this event are
 * water on its way through, sitting above the value the soil actually holds.
 */
export function isDrainContaminated(result) {
    return (result.kind === 'NO_PLATEAU' || result.kind === 'TOO_EARLY') && result.riseSeen;
}
/**
 * Drop the readings taken while the soil was draining, leaving only readings of
 * water the soil is actually holding.
 *
 * Any statistic over a raw reading history (a percentile, a mean) silently
 * assumes each reading is an independent look at soil moisture at rest. It is
 * not: for hours after every soak the probe is watching gravity work, and those
 * readings sit ABOVE field capacity by construction. On a slow-draining soil
 * that is a large, one-sided share of the history, so an upper percentile lands
 * in the band between field capacity and saturation and reports transient
 * gravitational water as the zone's "full".
 *
 * The fix is to censor the intervals where we KNOW the answer is contaminated:
 * from each event's start until its drainage should be done
 * ({@link DRAINAGE_HOURS} for the soil), truncated at the next event because
 * past that point the soil is being re-wet and the following window owns it.
 * What is left is soil at rest. This narrows the pool, which is why the
 * reading-count and day-span gates are re-applied to the filtered result rather
 * than the raw history.
 */
export function excludeDrainageWindows(readings, events, soilType) {
    if (events.length === 0)
        return readings;
    const sorted = [...events].sort((a, b) => Date.parse(a.start) - Date.parse(b.start));
    const drainMs = DRAINAGE_HOURS[soilType] * HOUR_MS;
    const windows = sorted.map((event, i) => {
        const next = sorted[i + 1];
        const from = Date.parse(event.start);
        const nextMs = next ? Date.parse(next.start) : Infinity;
        return { from, to: Math.min(Date.parse(event.end) + drainMs, nextMs) };
    });
    return readings.filter((r) => {
        const t = Date.parse(r.at);
        if (!Number.isFinite(t))
            return true;
        return !windows.some((w) => t >= w.from && t < w.to);
    });
}
//# sourceMappingURL=field-capacity.js.map